<template>
  <Modal :show="show" tabindex="-1" @close="closeModal" :enabled="!saving">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Extensions to my opportunity</h5>
        </div>
        <div class="modal-body">
          <div class="list-group owner-input mb-3" style="max-height: 50vh; overflow-y: auto">
            <div
              v-for="(extension, index) in filteredExtensions"
              :key="index"
              class="list-group-item list-group-item-action"
              style="cursor: pointer; display: flex; color: black"
            >
              <div
                class="colCheckboxRequest form-group mr-3 text-center"
                style="margin-bottom: 10px !important;"
              >
                <label class="kt-checkbox kt-checkbox--bold">
                  <input
                    type="checkbox"
                    v-model="extension.active"
                    :disabled="extension.disabled"
                  />
                  <span></span>
                </label>
              </div>
              <div style="width: 50%"><strong>{{ extension.extensiontitle }}</strong></div>
              <div><strong>{{ extension.numberofdays || '?' }} days</strong></div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-sm btn-wide btn-primary"
            :disabled="!canSave"
            @click="add"
          >
            <font-awesome-icon :icon="['fas', 'check']" class="mr-1" v-if="!saving && !adding" />
            <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-1" v-else /> ADD EXTENSIONS
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "@/components/_shared/Modal.vue";
import BaseInputForm from "@/components/_base/BaseInputForm.vue";
import {mapGetters} from "vuex";
import opportunityApi from '@/api/opportunity'
import cloneDeep from "lodash.clonedeep";

export default {
  name: "ExtensionSelectorModal",
  components: {BaseInputForm, Modal},
  data() {
    return {
      adding: false,
      enabledExtensions: [],
      opportunities: [],
      numberofdays: 0,
    };
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    extensions: {
      type: Array,
      default: [],
    },
    relatedOpportunities: {
      type: Array,
      default: [],
    },
    closeModal: {
      type: Function,
      default: () => {},
    },
    saving: {
      type: Boolean,
      default: false,
    },
    save: {
      type: Function,
      default: () => {},
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(show) {
        if (show) {
          this.numberofdays = this.itinerary.days.length;

          this.enabledExtensions = this.extensions.filter((item) => item.isselected)
            .map((item) => {
              return {
                ...item,
                active: false,
                disabled: false,
              };
            });

          this.opportunities = this.relatedOpportunities.filter((item) => item.total_days)
            .map(op => {
              return {
                ...op,
                id: 0,
                active: false,
                disabled: false,
              };
            });
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      opportunity: 'opportunity/getOpportunity',
      opportunitySource: 'opportunity/opportunitySource',
      opportunityData: 'opportunity/opportunity',
      itinerarySourceTypeSelected: 'opportunity/itinerarySourceTypeSelected',
    }),
    itinerary () {
      return this.opportunitySource.itineraries && this.opportunitySource.itineraries[this.itinerarySourceTypeSelected.index]
    },
    canSave() {
      return !this.adding && !this.saving && (
        this.enabledExtensions.filter((item) => item.active).length > 0 ||
        this.opportunities.filter((item) => item.active).length > 0
      );
    },
    filteredExtensions () {
      const defaultExtension = {
        extensiontitle: this.itinerary.titleitinerary,
        numberofdays: this.numberofdays,
        active: true,
        disabled: true,
      };

      return this.enabledExtensions.filter(f => f.type === 'preextension')
        .concat([defaultExtension])
        .concat(this.opportunities)
        .concat(this.enabledExtensions.filter(f => f.type === 'postextension'));
    },
  },
  methods: {
    parseItinerary(extension) {
      const text = extension.extensiondaytodaydetails
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&ndash;/gi, "-")
        .replace(/\n+/g, "\n")
        .trim();

      const blocks = text
        .split(/\n(?=Day\s+\d+)/i)
        .map(t => t.trim())
        .filter(Boolean);

      return blocks.map((block, index) => {
        const lines = block.split("\n").map(t => t.trim()).filter(Boolean);

        const title = lines[0];

        const mealsLine = lines.find(line =>
          /Included Meals/i.test(line)
        ) || "";

        const includedMeals = mealsLine
          .replace(/Included Meals[-\s]*/i, "")
          .trim();

        const meals = includedMeals === 'n/a' ? [] : includedMeals.split(',').map(meal => meal.trim());

        const description = lines
          .filter(line =>
            line !== title &&
            line !== mealsLine
          )
          .join(" ");

        const image = index === 0 ? extension.extensionimage : '';

        return {
          title: title.replace(/^Day\s+\d+\s*[–-]?\s*/i, "").trim(),
          textday: [{
            id: null,
            textday: description,
          }],
          locations: extension.extensioncity.locations,
          creation_source: "extension",
          image_url: image,
          url_image: image,
          source_itinerary_id: extension.id,
          source_day_id: parseInt(extension.extensionid),
          meals: meals.map(meal => {
            return {
              id: null,
              mealsday: meal,
            }
          }),
        };
      });
    },
    async add() {
      this.adding = true;

      const nDays = this.itinerary.days.length;
      const from = this.itinerary.fromitinerary;
      const filteredExtensions = cloneDeep(this.filteredExtensions);

      const beforeDays = filteredExtensions.filter(f => f.type === 'preextension')
        .reduce((acc, item) => acc + item.numberofdays, 0);

      if (beforeDays) {
        this.$store.commit('opportunity/TEMP_UPDATE_FROMITINERARY', beforeDays);

        await this.$store.dispatch(
          "opportunity/setOnlyOpportunity",
          {
            ...this.opportunity,
            departure_date: this.itinerary.fromitinerary,
          }
        );
      }

      let index = -1;
      for (const extension of filteredExtensions) {
        if (!extension.disabled && extension.active) {
          let days = [];

          if (extension.related_uuid) {
            const { data } = await opportunityApi.getPublishedOpportunity(extension.related_uuid);
            const publishedOpportunity = data.data;
            const itinerary = publishedOpportunity.itineraries[0];

            days = itinerary.days.map(day => {
              return {
                ...day,
                creation_source: "itinerary",
                source_itinerary_id: extension.id,
                source_day_id: parseInt(extension.extensionid),
                image_url: day.image_url,
                url_image: day.image_url,
              };
            });
          }
          else {
            days = this.parseItinerary(extension);
          }

          const offset = extension.type !== 'preextension' ? nDays : 0;

          days.forEach((day) => {
            const date = new Date(from + " 00:00:00");
            date.setDate(date.getDate() + index + offset);

            const startdate = date.toISOString().split("T")[0];

            const payload = {
              index: index + offset,
              day: {
                ...day,
                id: null,
                startdate: startdate,
                startday: index + offset + 1,
                isANewDay: true,
                image_duplicated: false,
                duration: 1,
              },
            };

            index++;

            this.$store.commit('opportunity/TEMP_ADD_DAY', payload)
            this.$store.commit('opportunity/TEMP_REFRESH_ORDER_DAYS', payload)
            this.$store.commit('opportunity/TEMP_REFRESH_DATE_DAYS', payload)

            const uuid = this.$route.params.privateId
            this.$store.dispatch('opportunity/saveOpportunityDataInLocalStorage', uuid);
          });
        }
      }

      this.save();

      this.adding = false;
    },
  }
}
</script>

<style scoped>

</style>
